import classnames from "classnames"

// Components
import { Input, TextMessage } from "@mobi/ds"
import IASelect from "./types"
import If from "@mobi/components/If"

// Hooks
import useSelect from "./hooks"

// Styles
import "./Select.scss"

// Icon
import ArrowDown from "../../static/icons/outlined/seta-baixo.svg"

const Select = (props: IASelect) => {
  const {
    customMessages,
    error = {
      type: "",
    },
    errorMessage,
    fieldId,
    hasErrorMessage,
    isEmptyList,
    isSearching,
    isSelected,
    isValid,
    options = [],
    required,
    native = false,
    defaultValue,
    handleOpenSelectClick,
    onBlur,
    onCustomChange,
    onFocus,
    onKeyDown,
    onSelectItem,
    getInputValue,
    shouldPlaceholderInvisible,
    field,
    selectedOptionIndex
  } = useSelect(props)

  const {
    name,
    label,
    disabled,
    type,
    "data-testid": data_testid,
    icon,
    isLoading,
    autoComplete,
    openOnFocus = true,
    isReadOnly,
    description,
    noSearch,
    isCombobox = false,
    placeholder,
  } = props

  const inputProps = {
    disabled,
    errorMessage: customMessages[error?.type],
    id: fieldId || name,
    isValid,
    label,
    description,
    name,
    "data-testid": data_testid,
    required,
    spellCheck: "false",
    type: "search",
    value: getInputValue(),
    onBlur,
    onChange: onCustomChange,
    onFocus,
    onKeyDown,
    tabIndex: "0",
    autoComplete: autoComplete || "off",
    "aria-autocomplete": "list",
    "aria-owns": `option-list-${fieldId}`,
    ...(options.length > 0 && {
      "aria-activedescendant": `option-${fieldId}_${
        selectedOptionIndex > 0 ? selectedOptionIndex : 0
      }`
    }),
    "aria-invalid": hasErrorMessage || "false",
    ...(hasErrorMessage && { "aria-describedby": `error-${name}` }),
    "aria-labelledby": `ds-label-${name}`,
    control: props.control,
    ...(isCombobox && {
      "role": "combobox",
      "aria-expanded": !isEmptyList,
      "aria-controls": `results-${fieldId}`,
    }),
    ...(type === "number" && {
      type: "text",
      inputMode: "numeric",
      pattern: "[0-9]*",
    }),
    ...((isReadOnly || noSearch) && { readOnly: true }),
    "aria-label": name,
    placeholder
  }
  
  return (
    <div
      className={classnames("ds-select", {
        "ds-select--invalid": !isValid,
        "ds-select--disabled": disabled,
        "ds-select--native": native,
        "ds-select--readOnly": isReadOnly,
        "ds-select--noSearch": noSearch,
      })}
    >
      <If
        condition={native}
        renderIf={
          <div className="ds-select__field">
            <label
              aria-label={description ? `${label} - ${description}` : label}
              className="ds-input__label"
              htmlFor={`ds-field-${props.name}`}
              id={`ds-label-${props.name}`}
            >
              {label}
            </label>
            <ArrowDown
              width="25px"
              height="25px"
              aria-hidden="true"
              className="ds-select__arrow-down "
            />
            <select
              disabled={disabled}
              name={name}
              required={required}
              id={`ds-field-${name}`}
              className={shouldPlaceholderInvisible() ? "invisiblePlaceholder" : ""}
              onFocus={onFocus}
              defaultValue={defaultValue || ""}
              onChange={(e) => {
                const value: string = e?.target?.value
                value === ""
                  ? e.target.classList.add("invisiblePlaceholder")
                  : e.target.classList.remove("invisiblePlaceholder")
                const option = options.find((item) => {
                  const isBoolean = typeof item.value == 'boolean'
                  const parsed = isBoolean ? item.value.toString() : item.value
                  return parsed == value
                })
                onSelectItem(option)
              }}
            >
              <option className="ds-select__item" selected disabled >
                Selecione uma opção
              </option>
              {options &&
                options.map((option, index) => {
                  return (
                    <option
                      key={index}
                      className="ds-select__item"
                      selected={option.value == defaultValue || option.value == field?.value || option.label == field?.value}
                      value={option.value}
                    >
                      {option.label}
                    </option>
                  )
                })}
            </select>
            <If
              condition={isValid && description}
              renderIf={
                <p className="ds-input__message ds-input__message--description">
                  {description}
                </p>
              }
            />
          </div>
        }
        renderElse={
          <>
            <div className="ds-select__field">
              <Input.Default {...inputProps} />

              <div
                id={`ds-select-button-${name}`}
                aria-hidden={true}
                className="ds-select__button-icon"
              >
                {icon ? (
                  icon
                ) : (
                  <ArrowDown
                    width="25px"
                    height="25px"
                    aria-hidden="true"
                    className={classnames("ds-select__arrow-down", {
                      "ds-select__arrow-down--spinned": options?.length,
                    })}
                  />
                )}
              </div>

              <If
                condition={isSearching || isLoading}
                renderIf={<div className="ds-select__loading"></div>}
              />
            </div>
            <div
              id={`option-list-${fieldId}`}
              className={classnames("ds-select__option-list", {
                "ds-select__option-list--hidden": isEmptyList,
              })}
            >
              <If
                condition={options.length > 0}
                renderIf={
                  <ul
                    id={`results-${fieldId}`}
                    className="ds-select__result-list"
                    role="listbox"
                    tabIndex={-1}
                    onKeyDown={onKeyDown}
                    aria-label={label}
                  >
                    {options.map((option, index) => {
                      const isSelected = index === selectedOptionIndex

                      return(
                        <li
                          id={`option-${fieldId}_${index}`}
                          className="ds-select__item"
                          onClick={() => onSelectItem(option)}
                          key={index}
                          role="option"
                          aria-selected={isSelected ? "true" : "false"}
                        >
                          {option.label}
                        </li>
                      )}
                    )}
                  </ul>
                }
              />
            </div>
            <div className="ds-select__feedback-msg" role="status">
              <If
                condition={options && options.length && !isSelected}
                renderIf={`${options ? options.length : ""
                  } sugestões encontradas, use as teclas para cima e para baixo para navegar`}
              />
            </div>

            <TextMessage
              id={`error-${name}`}
              show={hasErrorMessage}
              message={errorMessage}
            />
          </>
        }
      />
    </div>
  )
}

export default Select
